import { Button, Icon, Loading, Modal, Typography } from "@/components/atoms";
import { useApproveAccessMutation, useDeclineAccessMutation } from "@/redux/apis/assistants/assistantsApi";
import { AccessLink } from "@/redux/apis/assistants/types";
import { addToast } from "@/utils";
import { ClientAvatar } from "../../myClients/ClientAvatar";
import { formatRelativeShort } from "../../myClients/helpers";
import { useClientScope } from "../ClientScopeContext";

const CAN = ["Book, change, and cancel trips for you", "See and update your ride preferences", "See your booking history and trip details"];
const CANNOT = ["Can't change your password or remove your account"];

interface ReviewRequestModalProps {
  link: AccessLink | null;
  open: boolean;
  onClose: () => void;
}

/** Client-side "Review Request" modal — approve or decline an assistant's pending access request. */
export const ReviewRequestModal = ({ link, open, onClose }: ReviewRequestModalProps) => {
  // Operator-managed client id when set; undefined in self-service. Threaded through the mutations
  // so an operator approves/declines on the managed client's behalf.
  const { accountId } = useClientScope();
  const [approveAccess, { isLoading: isApproving }] = useApproveAccessMutation();
  const [declineAccess, { isLoading: isDeclining }] = useDeclineAccessMutation();
  const isLoading = isApproving || isDeclining;

  const firstName = link?.party.name?.split(" ")[0] || "This assistant";
  const requested = formatRelativeShort(link?.createdAt ?? null);

  const handle = async (action: "approve" | "decline") => {
    if (!link) return;
    try {
      if (action === "approve") {
        await approveAccess({ id: link.id, accountId }).unwrap();
        addToast("success", `${link.party.name} now has access to your account`);
      } else {
        await declineAccess({ id: link.id, accountId }).unwrap();
        addToast("success", `Declined ${link.party.name}'s request`);
      }
      onClose();
    } catch {
      addToast("danger", "Something went wrong. Please try again.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-medium"
      renderButtons={() => (
        <>
          <Button
            variant="custom"
            size="lg"
            className="bg-danger text-white hover:bg-danger-dark"
            onClick={() => handle("decline")}
            disabled={isLoading}
          >
            Decline Access
          </Button>
          <Button variant="primary" size="lg" onClick={() => handle("approve")} disabled={isLoading}>
            Approve Access
          </Button>
        </>
      )}
    >
      {isLoading && <Loading />}
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <ClientAvatar name={link?.party.name ?? ""} avatar={link?.party.avatar} size="md" />
          <div className="min-w-0">
            <Typography variant="action" className="block truncate font-semibold text-neutral-black">
              {link?.party.name}
            </Typography>
            <Typography variant="paragraph" className="block truncate text-neutral-dark-gray">
              {link?.party.email}
              {requested && <span> • Requested {requested} ago</span>}
            </Typography>
          </div>
        </div>

        <Typography variant="paragraph" className="text-neutral-black">
          {firstName} is requesting access to manage your RideMinder account on your behalf. If you approve, they&apos;ll be able to:
        </Typography>

        <div className="flex flex-col gap-3">
          {CAN.map((item) => (
            <div key={item} className="flex items-center gap-3">
              <Icon name="TickCircle" variant="Bold" size="md" className="shrink-0 text-success" />
              <Typography variant="paragraph" className="text-neutral-black">
                {item}
              </Typography>
            </div>
          ))}
          {CANNOT.map((item) => (
            <div key={item} className="flex items-center gap-3">
              <Icon name="CloseCircle" variant="Bold" size="md" className="shrink-0 text-danger" />
              <Typography variant="paragraph" className="text-neutral-black">
                {item}
              </Typography>
            </div>
          ))}
        </div>

        <div className="flex w-full items-center gap-2 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-2 py-1.5">
          <Icon name="InfoCircle" size="md" className="shrink-0 text-neutral-dark-gray" />
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            You can remove their access at any time from Settings → <span className="underline">Assistants</span>
          </Typography>
        </div>
      </div>
    </Modal>
  );
};
